import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageSquarePlus } from 'lucide-react';
import {
  calculatorConfig,
  isCalculatorAccessible,
  shouldShowBetaPill,
} from '../config/features';
import { landingFaqs } from '../config/seoContent';

/**
 * LandingPage — SEO-friendly hub that lists every public calculator
 * with a description and link. Gives Google a keyword-rich page to
 * crawl and gives first-time visitors an overview of all tools.
 */
const LandingPage = () => {
  const publicCalcs = Object.entries(calculatorConfig).filter(
    ([, calc]) => isCalculatorAccessible(calc) && calc.path !== '/logs' && !calc.excludeFromLandingGrid
  );

  return (
    <div className="w-full max-w-4xl mx-auto text-left">
      {/* The hero h1 is the only above-the-fold headline-font text on any
          prerendered page. Without a preload the font lands after first
          paint, the h1 re-wraps, and everything below it shifts (CLS). */}
      <Helmet>
        <link rel="preload" href="/fonts/TT_Supermolot_Neue_Condensed_Bold.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
      </Helmet>

      {/* Hero section */}
      <div className="mb-8">
        <h1 className="font-headline font-bold text-2xl sm:text-3xl text-ink mb-3 text-center tracking-tight">
          Professional Video Production Calculators
        </h1>
        <p className="text-sm sm:text-base text-ink-secondary leading-relaxed text-center max-w-2xl mx-auto">
          Free online calculators built for editors, colorists, DITs, producers, and filmmakers.
          Estimate file sizes, convert timecode, plan storage, calculate transfer times, and more.
        </p>
      </div>

      {/* Calculator cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {publicCalcs.map(([key, calc]) => (
          <Link
            key={key}
            to={calc.path}
            className="group flex items-start gap-4 p-4 rounded-xl border border-border bg-surface-card hover:border-conform-yellow/30 hover:shadow-md transition-all duration-200 no-underline"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-conform-yellow/10 text-conform-yellow group-hover:bg-conform-yellow/20 transition-colors flex-shrink-0 mt-0.5">
              {React.cloneElement(calc.icon, { size: 20 })}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-sm font-semibold text-ink group-hover:text-conform-yellow transition-colors">
                  {calc.title}
                </h2>
                {shouldShowBetaPill(calc) && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-conform-red/10 text-conform-red uppercase tracking-wider">
                    Beta
                  </span>
                )}
              </div>
              <p className="text-xs text-ink-secondary leading-relaxed line-clamp-2">
                {calc.description}
              </p>
            </div>
          </Link>
        ))}
      </div>

      {/* Request CTA card */}
      <Link
        to="/request"
        className="group flex items-start gap-4 p-4 mt-4 rounded-xl border-2 border-dashed border-conform-yellow/30 bg-conform-yellow/5 hover:border-conform-yellow/50 hover:bg-conform-yellow/10 hover:shadow-md transition-all duration-200 no-underline"
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-conform-yellow/20 text-conform-yellow group-hover:bg-conform-yellow/30 transition-colors flex-shrink-0 mt-0.5">
          <MessageSquarePlus size={20} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-sm font-semibold text-conform-yellow group-hover:text-conform-yellow transition-colors">
              Submit a Request
            </h2>
          </div>
          <p className="text-xs text-ink-secondary leading-relaxed line-clamp-2">
            Have a feature idea, bug report, or general feedback? We'd love to hear from you — let us know how we can improve.
          </p>
        </div>
      </Link>

      {/* SEO footer text */}
      <div className="mt-10 pt-6 border-t border-border-light">
        <h2 className="font-headline font-bold text-base text-ink mb-3">About DataCalc</h2>
        <div className="text-sm text-ink-secondary leading-relaxed space-y-3">
          <p>
            <strong>DataCalc by CONFORM.TOOLS</strong> is a free suite of professional video production
            calculators. Whether you need to estimate how much storage a shoot day requires, figure out
            how long a file transfer will take, or convert between video duration and file size, DataCalc
            has you covered.
          </p>
          <p>
            Our tools support cameras from ARRI, RED, Blackmagic, Sony, Canon, and more. Calculate
            with industry-standard codecs including ProRes, ARRIRAW, REDCODE, DNxHD, and H.264/H.265.
            The Timecode Calculator lets you add, subtract, multiply, and divide timecode at any frame rate.
          </p>
          <p>
            Every calculation can be logged and shared with your team. DataCalc works offline as an
            installable PWA, so your tools are always at your fingertips — on set, in the edit bay, or
            anywhere in between.
          </p>
          <p>
            {/* No {' '} here: adjacent JSX text nodes break hydration of
                react-snap HTML (serialized DOM merges them) */}
            DataCalc is built by <a
              href="https://conform.tools/?utm_source=data-calc&utm_medium=referral&utm_campaign=landing"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-conform-red hover:text-conform-yellow transition-colors"
            >CONFORM.TOOLS</a>, makers of professional conform and post-production software.
            Check out our other tools for editors, assistant editors, and finishing artists.
          </p>
        </div>
      </div>

      {/* FAQ — visible content backing the FAQPage schema emitted by SEOHead */}
      <div className="mt-10 pt-6 border-t border-border-light">
        <h2 className="font-headline font-bold text-base text-ink mb-4">Frequently Asked Questions</h2>
        <div className="space-y-5">
          {landingFaqs.map(({ q, a }) => (
            <div key={q}>
              <h3 className="text-sm font-semibold text-ink mb-1">{q}</h3>
              <p className="text-sm text-ink-secondary leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
